/**
 * Iter97: NotificationBell — Glocke in der Topbar mit Badge + Dropdown.
 *
 * Pollt alle 60s ungelesene Notifications, zeigt Anzahl als Badge.
 * Klick: Dropdown mit den letzten 10. Klick auf Item: navigiert zu action_url.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Bell, AlertTriangle, AlertCircle, CheckCheck } from "lucide-react";

const POLL_MS = 60000;
const SEVERITY_ICON = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Bell,
};
const SEVERITY_TONE = {
  error: "text-rose-600 bg-rose-50",
  warning: "text-amber-700 bg-amber-50",
  info: "text-sky-700 bg-sky-50",
};

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "gerade eben";
    if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min.`;
    if (sec < 86400) return `vor ${Math.floor(sec / 3600)} Std.`;
    return d.toLocaleDateString("de-CH");
  } catch {
    return iso;
  }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get("/notifications?limit=15");
      setItems(r.data || []);
      setUnread((r.data || []).filter((n) => !n.read).length);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Click-outside-to-close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await apiClient.post(`/notifications/${n.id}/read`);
      } catch {}
    }
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
    load();
  };

  const markAllRead = async () => {
    try {
      await apiClient.post("/notifications/read-all");
      load();
    } catch {}
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-button"
      >
        <Bell className="h-4.5 w-4.5 text-slate-700" />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[480px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Benachrichtigungen</h3>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="h-3 w-3" /> Alle gelesen
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-500">
              Keine Benachrichtigungen
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const Icon = SEVERITY_ICON[n.severity] || Bell;
                const tone = SEVERITY_TONE[n.severity] || SEVERITY_TONE.info;
                return (
                  <li
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className={`px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${
                      !n.read ? "bg-violet-50/40" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`flex-shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center ${tone}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-semibold text-slate-900 truncate">{n.title}</div>
                          {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-violet-500 flex-shrink-0" />}
                        </div>
                        <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{fmtRel(n.created_at)}</div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
